import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpDown,
  BarChart3,
  Building2,
  Calendar,
  ChevronRight,
  Clock,
  Package,
  ShieldCheck
} from "lucide-react";

function TutorialStep({ number, title, children }) {
  return (
    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden text-left [&_p]:!text-justify [&_li]:!text-justify">
      <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
      <h3 className="text-xl font-bold text-gray-900 mt-0 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black shrink-0">
          {number}
        </span>
        {title}
      </h3>
      <div className="text-gray-600">{children}</div>
    </div>
  );
}

export default function InventoryTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Gestão Operacional</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Controle de Estoque
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>6 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão Operacional
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-left [&_p]:!text-justify [&_li]:!text-justify">

          <p className="lead text-xl text-gray-700 font-medium">
            Controle materiais, medicamentos, fornecedores e movimentações
            do estoque da clínica de forma organizada.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>

          <div className="space-y-8 mt-8">
          <TutorialStep number="1" title="Visão Geral">

          <p>
            O módulo de Estoque foi criado para ajudar no controle operacional
            da clínica, reduzindo perdas e melhorando o acompanhamento dos materiais utilizados.
          </p>

          <div className="mt-5">
            <h4 className="flex items-center gap-3 text-base font-bold text-indigo-700 mt-0 mb-3">
              <Package className="shrink-0" size={22} />
              Recursos disponíveis
            </h4>

            <ul>
              <li>Cadastro de itens;</li>
              <li>Controle de quantidades;</li>
              <li>Controle de validade;</li>
              <li>Movimentações;</li>
              <li>Fornecedores;</li>
              <li>Alertas de estoque baixo;</li>
              <li>Relatórios e estatísticas.</li>
            </ul>
          </div>
          </TutorialStep>

          <TutorialStep number="2" title="Cadastrando um Item">

          <p>
            Clique em <strong>Novo Item</strong> para cadastrar um produto ou material.
          </p>

          <p>
            O sistema permite informar:
          </p>

          <ul>
            <li>nome;</li>
            <li>categoria;</li>
            <li>marca;</li>
            <li>modelo;</li>
            <li>unidade;</li>
            <li>estoque mínimo;</li>
            <li>custo;</li>
            <li>validade;</li>
            <li>lote;</li>
            <li>fornecedor;</li>
            <li>localização no estoque.</li>
          </ul>
          </TutorialStep>

          <TutorialStep number="3" title="Controle de Estoque Baixo">

          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="text-amber-600" size={24} />
              <h4 className="m-0 text-base font-bold text-amber-900">
                Alertas automáticos
              </h4>
            </div>

            <p className="text-amber-900">
              Quando a quantidade atual ficar abaixo do estoque mínimo,
              o sistema sinaliza automaticamente o item como estoque baixo.
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="4" title="Registrando Movimentações">

          <p>
            Utilize o botão <strong>Nova Movimentação</strong> para registrar:
          </p>

          <ul>
            <li>entradas;</li>
            <li>saídas;</li>
            <li>ajustes;</li>
            <li>perdas;</li>
            <li>vencimentos.</li>
          </ul>

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mt-5">
            <p className="m-0 text-blue-900 flex items-start gap-3">
              <ArrowUpDown className="text-blue-600 shrink-0 mt-1" size={21} />
              <span>
              <strong>Histórico operacional:</strong> todas as movimentações ficam registradas no histórico do estoque,
              incluindo data, quantidade, motivo e usuário responsável.
              </span>
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="5" title="Controle de Validade">

          <p>
            Itens com validade cadastrada podem ser monitorados automaticamente.
          </p>

          <div className="bg-red-50 border border-red-200 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="text-red-600" size={24} />
              <h4 className="m-0 text-base font-bold text-red-900">
                Produtos próximos ao vencimento
              </h4>
            </div>

            <p className="text-red-900">
              O sistema identifica produtos próximos ao vencimento,
              ajudando a evitar perdas e desperdícios.
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="6" title="Cadastro de Fornecedores">

          <p>
            A aba de fornecedores permite cadastrar:
          </p>

          <ul>
            <li>nome;</li>
            <li>contato;</li>
            <li>telefone;</li>
            <li>e-mail;</li>
            <li>CNPJ;</li>
            <li>observações.</li>
          </ul>

          <div className="bg-green-50 border border-green-100 rounded-2xl p-5 mt-5">
            <p className="m-0 text-green-900 flex items-start gap-3">
              <Building2 className="text-green-600 shrink-0 mt-1" size={21} />
              <span><strong>Organização operacional:</strong> o cadastro de fornecedores facilita compras, reposições e controle administrativo.</span>
            </p>
          </div>
          </TutorialStep>

          <TutorialStep number="7" title="Relatórios">

          <p>
            A área de relatórios apresenta indicadores do estoque da clínica.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6">
            <h4 className="flex items-center gap-3 text-base font-bold text-indigo-900 mt-0 mb-3">
              <BarChart3 className="shrink-0" size={22} />
              Indicadores automáticos
            </h4>

            <ul className="text-indigo-900">
              <li>Total de itens;</li>
              <li>Estoque baixo;</li>
              <li>Produtos próximos ao vencimento;</li>
              <li>Valor total do estoque;</li>
              <li>Movimentações recentes.</li>
            </ul>
          </div>
          </TutorialStep>

          <TutorialStep number="8" title="Recomendações">

          <div className="bg-green-50 border border-green-200 rounded-3xl p-6">
            <h4 className="flex items-center gap-3 text-base font-bold text-green-900 mt-0 mb-3">
              <ShieldCheck className="shrink-0" size={22} />
              Boas práticas
            </h4>

            <ul className="text-green-900">
              <li>Atualize o estoque diariamente;</li>
              <li>Registre movimentações imediatamente;</li>
              <li>Revise itens próximos ao vencimento;</li>
              <li>Configure estoque mínimo corretamente;</li>
              <li>Padronize nomes e categorias.</li>
            </ul>
          </div>
          </TutorialStep>
          </div>

        </article>
      </main>
    </div>
  );
}
